import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Field, Form, FormRenderProps } from 'react-final-form';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { FieldTextInput } from 'Components/FormFields';
import UIButton from 'Components/UIComponents/UIButton';
import UISpinner from 'Components/UIComponents/UISpinner';
import arrayMutators from 'final-form-arrays';
import useIsMobile from 'Hooks/Common/useIsMobile';
import useDocumentCreateFromFormRequest from 'Hooks/Document/useDocumentCreateFromFormRequest';
import useFormRequestGet from 'Hooks/Document/useFormRequestGet';
import useFormRequestGuard from 'Hooks/Document/useFormRequestGuard';
import { Document } from 'Interfaces/Document';
import { AuthorizedRoutePaths } from 'Interfaces/RoutePaths';
import Toast from 'Services/Toast';
import { removeEmptyCharacters } from 'Utils/formatters';
import { composeValidators } from 'Utils/functions';
import { selectDocumentWithCompany } from 'Utils/selectors';
import { email, notOnlySpaces, required } from 'Utils/validation';

import ThanksMessage from './components/ThanksMessage';

import Logo from 'Assets/images/logo.svg';

type FormRequestRouteParams = {
  formRequestId: Document['id'];
};

const FormRequestShow = () => {
  const navigate = useNavigate();
  const params = useParams<FormRequestRouteParams>();

  const documentId = params.formRequestId as string;
  const [getDocument] = useFormRequestGet();
  const document = useSelector(state => selectDocumentWithCompany(state, { documentId }));
  const [createDocument] = useDocumentCreateFromFormRequest();
  const isMobile = useIsMobile();

  const [thanksMessage, setThanksMessage] = useState<boolean>(false);

  const initialValues = useMemo(() => ({ documentId }), [documentId]);

  const handleDocumentShare = useCallback(async values => {
    try {
      await createDocument({
        templateId: documentId,
        signer: { email: values.email, name: values.name },
      });
      // Toast.success('Document successfully sended to specified email(s).');
      setThanksMessage(true);
    } catch (err) {
      Toast.handleErrors(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getDocument({ documentId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDocumentNotFound = useCallback(() => {
    navigate(AuthorizedRoutePaths.FORM_REQUESTS);
  }, [navigate]);

  const isCheckingDocument = useFormRequestGuard({
    documentId,
    onFailure: handleDocumentNotFound,
  });

  if (thanksMessage) {
    return <ThanksMessage companyLogo={document?.company.logo} />;
  }

  if (isCheckingDocument) {
    return <UISpinner wrapperClassName="spinner--main__wrapper" width={50} height={50} />;
  }
  return (
    <div className="form-request__content">
      <div className="form-request">
        <Helmet>
          <meta name="description" content="Log in to Signaturely." />
          <title>Send Document</title>
        </Helmet>
        <Form
          initialValues={initialValues}
          onSubmit={handleDocumentShare}
          mutators={{ ...arrayMutators }}
          render={({
            handleSubmit,
            submitting,
            pristine,
            valid,
          }: FormRenderProps<any>) => (
            <form className="form-request__form--border">
              <div
                className={classNames('form-request__form', 'form-request__form--login', {
                  mobile: isMobile,
                })}
              >
                <div className="form-request__logo">
                  {document?.company.logo ? (
                    <img src={document.company.logo} alt="" />
                  ) : (
                    <img src={Logo} alt="Signaturely" />
                  )}
                </div>

                <div className="form-request__documentTitle">{document?.title}</div>
                <div className="form-request__documentMessage">{document?.message}</div>

                <div className="form-request__hr"></div>
                <div className="form-request__separator"></div>

                <Field
                  label="Name"
                  name="name"
                  placeholder="Name"
                  component={FieldTextInput}
                  validate={composeValidators<string>(required, notOnlySpaces)}
                />
                <Field
                  label="Email Address"
                  name="email"
                  placeholder="Email"
                  component={FieldTextInput}
                  parse={removeEmptyCharacters}
                  validate={composeValidators<string>(required, email)}
                />
                <div className="form-request__submitButton">
                  <UIButton
                    priority="primary"
                    handleClick={handleSubmit}
                    disabled={pristine || !valid || submitting}
                    isLoading={submitting}
                    type="submit"
                    title="Continue"
                  />
                </div>
              </div>
              <div className="form-request__footer">
                <a
                  href="https://signaturely.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: 'none',
                  }}
                >
                  <span>Powered by</span>
                  <span className="form-request__footer--signaturely">Signaturely</span>
                </a>
              </div>
            </form>
          )}
        />
      </div>
    </div>
  );
};

export default FormRequestShow;
